import { styled } from "@mui/material/styles";
import { Box, Button, IconButton, TextField, Typography } from "@mui/material";

export const SidebarRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  width: "300px",
  minWidth: "260px",
  height: "100%",
  borderRight: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  [theme.breakpoints.down("md")]: {
    width: "100%",
    minWidth: 0,
    borderRight: "none",
  },
}));

export const SidebarHeader = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  padding: "16px 0",
}));

export const SidebarTitleRow = styled(Box)(() => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: "10px",
  paddingBottom: "16px",
  paddingLeft: "16px",
  paddingRight: "16px",
}));

export const NewChatSection = styled(Box)(({ theme }) => ({
  padding: "16px",
  borderTop: `1px solid ${theme.palette.divider}`,
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

export const SearchSection = styled(Box)(() => ({
  padding: "0 16px",
  paddingTop: "16px",
}));

export const HeaderRow = styled(Box)(() => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: "10px",
}));

export const HeaderAIIconSm = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "32px",
  height: "32px",
  borderRadius: "4px",
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.textInput,
  fontWeight: 700,
  fontSize: "13px",
  flexShrink: 0,
}));

export const SidebarTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontWeight: 700,
  fontSize: "16px",
}));

export const NewChatButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  borderRadius: "8px",
  textTransform: "none",
  fontWeight: 600,
  fontSize: "14px",
  padding: "10px 16px",
  boxShadow: "none",
  justifyContent: "center",
  "&:hover": {
    backgroundColor: theme.palette.primary.dark ?? theme.palette.primary.main,
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    backgroundColor: theme.palette.action.disabledBackground,
    color: theme.palette.action.disabled,
  },
}));

export const SearchInput = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.primary,
    fontSize: "14px",
    "& fieldset": { borderColor: theme.palette.divider },
    "&:hover fieldset": { borderColor: theme.palette.divider },
    "&.Mui-focused fieldset": { borderColor: theme.palette.primary.main },
  },
  "& .MuiInputBase-input::placeholder": {
    color: theme.palette.text.tertiary,
    opacity: 1,
  },
}));

type ConversationListProps = { withTopBorder?: boolean };

export const ConversationList = styled(Box, {
  shouldForwardProp: (prop) => prop !== "withTopBorder",
})<ConversationListProps>(({ theme, withTopBorder = true }) => ({
  flex: 1,
  overflowY: "auto",
  scrollbarWidth: "thin",
  borderTop: withTopBorder ? `1px solid ${theme.palette.divider}` : "none",
}));

type ConversationItemProps = { active: boolean };

export const ConversationItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== "active",
})<ConversationItemProps>(({ theme, active }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "flex-start",
  gap: "10px",
  padding: "15px 16px",
  cursor: "pointer",
  color: theme.palette.text.primary,
  backgroundColor: active ? theme.palette.action.selected : "transparent",
  transition: "background-color 120ms ease",
  borderBottom: `1px solid ${theme.palette.divider}`,
  "&:hover": {
    backgroundColor: active
      ? theme.palette.action.selected
      : theme.palette.action.hover,
  },
  "& > svg": {
    marginTop: "2px",
    flexShrink: 0,
    opacity: 0.8,
  },
}));

export const ConversationItemContent = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minWidth: 0,
}));

export const ConversationTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontWeight: 600,
  fontSize: "13px",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
}));

export const ConversationPreview = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: "12px",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  minHeight: "1.2em",
}));

export const ConversationDate = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.tertiary,
  fontSize: "11px",
  marginTop: "2px",
  minHeight: "1.2em",
}));

export const DeleteIcon = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.tertiary,
  opacity: 0,
  transition: "opacity 120ms ease",
  alignSelf: "center",
  ".MuiBox-root:hover > &": {
    opacity: 1,
  },
  "@media (hover: none)": {
    opacity: 1,
  },
  "&:hover": {
    color: theme.palette.error.main,
    backgroundColor: theme.palette.action.hover,
  },
}));

export const EmptyState = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.tertiary,
  fontSize: "13px",
  textAlign: "center",
  padding: "16px",
}));

export const HistoryDisabledContainer = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "10px",
  padding: "32px 20px",
  textAlign: "center",
}));

export const HistoryDisabledIcon = styled(Box)(({ theme }) => ({
  width: "44px",
  height: "44px",
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: theme.palette.action.hover,
  color: theme.palette.text.tertiary,
  marginBottom: "4px",
}));

export const HistoryDisabledTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: "14px",
  fontWeight: 600,
}));

export const HistoryDisabledSubtitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.tertiary,
  fontSize: "12px",
  lineHeight: 1.5,
  maxWidth: "260px",
}));

export const LoadingRow = styled(Box)(() => ({
  display: "flex",
  justifyContent: "center",
  padding: "16px",
}));

export const SidebarFooter = styled(Box)(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.divider}`,
  padding: "12px 16px",
}));

export const SettingsButton = styled(Button)(({ theme }) => ({
  color: theme.palette.text.primary,
  textTransform: "none",
  fontWeight: 500,
  fontSize: "14px",
  padding: "6px 8px",
  display: "flex",
  alignItems: "center",
  gap: "8px",
  justifyContent: "flex-start",
}));
